/**
 * The harbour tour's progress, as the server holds it (`GET /api/tutorial`).
 * Resumable on any device; the overlay plays the script for the current step
 * and asks the server to complete it.
 *
 * Owns: the one copy of the view and the calls that change it. Must not:
 * decide when a step is done (the overlay says what the captain did; the
 * server accepts or refuses) or draw anything (`ui/tutorial/TutorialOverlay.vue`).
 *
 * It also holds the battle lesson's "done" flag (`trainingDone`), which used to
 * be a browser-local key: the lesson teaches the keys, but whether a captain
 * has been through it is a fact about the account.
 */
import { computed, ref, watch } from 'vue';

import type { TourStep } from '@shared/harbour-tour';
import { api, type TutorialView } from '@/net/api';
import { useSession } from './session';

const session = useSession();

const view = ref<TutorialView | null>(null);
let loading: Promise<void> | null = null;

/** The tour is running: a view exists and neither finished nor skipped. */
export const tourActive = computed(() => !!view.value && !view.value.finished && !view.value.skipped);
export const tourStep = computed<TourStep | null>(() => view.value?.step ?? null);
export const tourView = computed(() => view.value);

/**
 * Whether the training sortie is behind the captain. Unknown (not loaded, or the
 * server unreachable) counts as done, so a slow answer never sends a veteran to school.
 */
export const trainingDone = computed(() => view.value?.trainingDone ?? true);

/** Reads the progress once per session; `force` re-reads (after a replay elsewhere). */
export function loadTour(force = false): Promise<void> {
  if (loading && !force) return loading;
  loading = api
    .tutorial()
    .then((v) => {
      view.value = v;
    })
    .catch(() => {
      // No tour is better than a broken harbour: the next mount asks again.
      loading = null;
    });
  return loading;
}

/** Completes `step`. Returns false when the server refused (wrong order, not at sea yet). */
export async function completeTourStep(step: TourStep): Promise<boolean> {
  try {
    view.value = await api.completeTutorialStep(step);
    return true;
  } catch {
    return false;
  }
}

export async function skipTour(): Promise<void> {
  try {
    view.value = await api.skipTutorial();
  } catch {
    // Still running; the captain can press skip again.
  }
}

export async function replayTour(): Promise<boolean> {
  try {
    view.value = await api.replayTutorial();
    return true;
  } catch {
    return false;
  }
}

/** The battle lesson ended or was skipped: remember it on the account. */
export async function markTrainingDone(): Promise<void> {
  try {
    view.value = await api.tutorialTrainingDone();
  } catch {
    // The lesson is offered once more next time rather than lost.
  }
}

/** Sign-out: nothing of this captain's progress carries to the next. */
export function resetTour(): void {
  view.value = null;
  loading = null;
}

watch(() => session.player.value?.id, resetTour);
